import React, { useEffect, useState, useRef } from 'react';
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api';
import axios from 'axios';
import {HEADERS, ENDPOINTS} from './apiConfig';

function toggleRows() {
    let rows = document.querySelectorAll(".visitingRow");
    let innerCollapse = document.querySelector(".innerCollapse");

    if (window.innerWidth <= 767) { // Ensure it runs only on max 767px
        if (innerCollapse && innerCollapse.classList.contains("show")) {
            rows.forEach(item => item.style.display = "block");
        } else {
            rows.forEach((item, index) => {
                item.style.display = index < 1 ? "block" : "none";
            });
        }
    } else {
        rows.forEach(item => item.style.display = "block"); // Show all rows on larger screens
    } window.addEventListener("resize", toggleRows); // Re-check when resizing the window

    document.querySelectorAll(".innerCollapse").forEach(collapse => {
        collapse.addEventListener("shown.bs.collapse", toggleRows);
        collapse.addEventListener("hidden.bs.collapse", toggleRows);
    });
}




function MapComponent({vocabularyId,contentKey,googleMapAPIKey}) {
    // wait for a valid API key first
    if (!googleMapAPIKey) return <p>Loading map...</p>;

    const { isLoaded } = useJsApiLoader({
        id: 'google-map-script',
        googleMapsApiKey: googleMapAPIKey,
        // optionally: libraries: ['places'],
    });


    const [map, setMap] = useState(null);
    const prevMapState = useRef(null);


    const [facilities, setFacilities] = useState([]);
    const [categories, setCategories] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [autocompleteResults, setAutocompleteResults] = useState([]);
    const [selectedFacility, setSelectedFacility] = useState(null);
    const [categoryFilter, setCategoryFilter] = useState('');
    const [showResults, setShowResults] = useState(false);
    const [mapCollapseContent, setMapCollapseContent] = useState("");
    const [center, setCenter] = useState({lat: 25.25, lng: 55.3});
    const [zoom, setZoom] = useState(8); // Default zoom level
    const [language, setLanguage] = useState(Liferay.ThemeDisplay.getLanguageId().replace('_', '-'));

    const scopeKey = Liferay.ThemeDisplay.getScopeGroupId();

    useEffect(() => {
        const checkLanguageChange = setInterval(() => {
            const currentLanguage = Liferay.ThemeDisplay.getLanguageId().replace('_', '-');
            if (language !== currentLanguage) {
                setLanguage(currentLanguage);
            }
        }, 1000); // Check every second

        return () => clearInterval(checkLanguageChange);
    }, [language]);

    // Fetch web content by key
    const fetchWebContent = async () => {
        try {
            const response = await axios.get(`${ENDPOINTS.webContentByKey(scopeKey, contentKey)}`, { headers: HEADERS });
            const contentField = response.data.contentFields.find(
                (field) => field.name === "content"
            );
            const htmlContent = contentField?.contentFieldValue?.data || "<p>No content available</p>";
            setMapCollapseContent(htmlContent);
        } catch (error) {
            console.error("Error fetching web content by key:", error);
        }
    };

    // For sorting the Visiting hours
    const dayOrder = {
        Monday: 1,
        Tuesday: 2,
        Wednesday: 3,
        Thursday: 4,
        Friday: 5,
        Saturday: 6,
        Sunday: 7,
    };

    // Formatting the Visiting Hours in desired format
    const formatWorkingHours = (startTimeISO, endTimeISO, secondStartTimeISO, secondEndTimeISO, open24Hours) => {
        if (!startTimeISO || !endTimeISO) {
            return null;
        }

        const locale = Liferay.ThemeDisplay.getLanguageId().replace('_', '-');

        const startTime = new Date(startTimeISO);
        const endTime = new Date(endTimeISO);
        const day = startTime.toLocaleDateString(locale, { weekday: "long" });

        const startTimeFormatted = startTime.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: true });
        const endTimeFormatted = endTime.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: true });

        let secondTimeSlot = null;
        if (secondStartTimeISO && secondEndTimeISO) {
            const secondStartTime = new Date(secondStartTimeISO);
            const secondEndTime = new Date(secondEndTimeISO);

            secondTimeSlot = {
                start: secondStartTime.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: true }),
                end: secondEndTime.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit", hour12: true })
            };
        }

        return {
            day,
            firstSlot: `${startTimeFormatted} - ${endTimeFormatted}`,
            secondSlot: secondTimeSlot ? `${secondTimeSlot.start} - ${secondTimeSlot.end}` : null,
            hasSecondSlot: !!secondTimeSlot,
            sortOrder: dayOrder[day] || 99,
            open24hours: open24Hours
        };
    };

    const applyRightSideOffset = (mapInstance) => {
        if (!mapInstance) return;

        if (window.innerWidth <= 767) return; // only for desktop

        const overlay = document.querySelector('.serch_box_wrapper');
        const overlayWidth = overlay ? overlay.offsetWidth : 0;
        if (!overlayWidth) return;

        const dpr = window.devicePixelRatio || 1;
        const offsetX = Math.round(overlayWidth * 0.4 * dpr);

        // Use the passed map instance
        animatePanBy(mapInstance, -offsetX, 0, 700);
    };

    const applyMobileTopOffset = (mapInstance, currentCenter, factor = 0.1) => {
        if (!mapInstance || !currentCenter) return currentCenter;

        // factor defines upward shift amount (0.04–0.10 works well)
        const newLat = currentCenter.lat - factor; // + moves map upward visually

        const newCenter = { lat: newLat, lng: currentCenter.lng };

        mapInstance.setCenter(newCenter);
        return newCenter;
    };


    // Fetch facilities
    const fetchFacilities = async () => {
        try {
            const response = await axios.get(`${ENDPOINTS.facilities(scopeKey)}`, { headers: HEADERS });
            const facilitiesData = await Promise.all(
                response.data.items.map(async (facility) => {
                    try {
                        const visitingHoursResponse = await axios.get(
                            `${ENDPOINTS.visitingHours(facility.id)}`,
                            { headers: HEADERS }
                        );

                        const visitingHours = visitingHoursResponse.data.items
                            .map((visitingHour) => formatWorkingHours(visitingHour.startTime, visitingHour.endTime, visitingHour.secondStartTime, visitingHour.secondEndTime, visitingHour.open24Hours))
                            .filter((hour) => hour !== null)
                            .sort((a, b) => a.sortOrder - b.sortOrder);

                        return {
                            id: facility.id,
                            name: facility.name,
                            lat: parseFloat(facility.latitude),
                            lng: parseFloat(facility.longitude),
                            address: facility.location,
                            phone: facility.contactNo,
                            emergencyTimings: facility.emergencyTimings,
                            visitingHours,
                            category: facility.taxonomyCategoryBriefs.map((cat) => cat.taxonomyCategoryName).join(", "),
                            friendlyUrlPath: facility.friendlyUrlPath
                        };
                    } catch (error) {
                        console.error(`Error fetching visiting hours for facility ${facility.id}:`, error);
                        return null;
                    }
                })
            );

            // Filter out invalid facilities
            const validFacilities = facilitiesData.filter((facility) => facility && facility.lat && facility.lng);
            setFacilities(validFacilities);
            const hospitalKeywords = ["Hospitals", "المستشفيا", "Hospital","المستشفيات"]; // Add more as needed

            if (isLoaded && validFacilities.length > 0) {
                // Find the first facility matching any hospital keyword
                const defaultHospital = validFacilities.find((facility) =>
                    hospitalKeywords.some((keyword) => facility.category.includes(keyword))
                );

                // Fallback to the first facility if no hospital is found
                const initialCenter = defaultHospital || validFacilities[0];
                //setCenter({ lat: initialCenter.lat, lng: initialCenter.lng });
            }
        } catch (error) {
            console.error("Error fetching facilities:", error);
        }
    };




    // Fetch categories
    const fetchCategories = async () => {
        try {
            const response = await axios.get(`${ENDPOINTS.categories(vocabularyId)}`, { headers: HEADERS });
            const fetchedCategories = response.data.items;
            setCategories(fetchedCategories);  // Set categories in state

            // Set the first category as default selected (if categories exist)
            if (fetchedCategories.length > 0) {
                setCategoryFilter(fetchedCategories[0].name);  // Default to the first category
            }
        } catch (error) {
            console.error("Error fetching categories:", error);
        }
    };

    // Resize the 'Where To Find Us' modal as per screen size
    const adjustOnResize = () => {
        var topHeader = document.querySelector('.dubaiHealthHeader');
        var topHeaderHeight = topHeader ? topHeader.offsetHeight : 0;

        var stickyFooter = document.querySelector('.footerBottomMenuFixed');
        var stickyFooterHeight = stickyFooter ? stickyFooter.offsetHeight : 0;
        var newHeight = `calc(100vh - 110px - ${topHeaderHeight + stickyFooterHeight}px)`;

        var searchBoxWrapper = document.querySelector('.serch_box_wrapper');
        if (searchBoxWrapper) {
            searchBoxWrapper.style.height = newHeight;
        }
        var mapBody = document.querySelector('#dvMap');
        mapBody.style.height = `calc(100vh - ${topHeaderHeight + stickyFooterHeight}px)`;
    };


    const handleBack = (e) => {
        if (e && e.preventDefault) e.preventDefault();

        // Close info panel
        setSelectedFacility(null);

        // Restore previous map state if available
        if (prevMapState.current && map) {
            const { center: prevCenter, zoom: prevZoom } = prevMapState.current;

            // Update React state
            setCenter(prevCenter);
            setZoom(prevZoom);

            // Update actual google map
            try {
                map.setCenter(prevCenter);
                map.setZoom(prevZoom);
            } catch (ignored) {}

            // clear saved state so next marker click saves fresh state
            prevMapState.current = null;
        } else {
            // Fallback: re-apply current category filter or fit all markers
            if (categoryFilter) {
                handleCategoryFilter(categoryFilter);
            } else if (map && facilities.length) {
                const bounds = new window.google.maps.LatLngBounds();
                facilities.forEach(f => bounds.extend({ lat: f.lat, lng: f.lng }));
                map.fitBounds(bounds);
            }
        }

        setTimeout(toggleRows, 100);
    };



    const handleMarkerClick = (facility) => {
        setSelectedFacility(facility);

        if (!map) return;

        // Save current map state so we can restore on "back"
        try {
            const centerObj = map.getCenter();
            prevMapState.current = {
                center: centerObj ? centerObj.toJSON() : { ...center },
                zoom: map.getZoom()
            };
        } catch (err) {
            prevMapState.current = { center: { ...center }, zoom };
        }

        // center + zoom to selected facility
        map.panTo({ lat: facility.lat, lng: facility.lng });
        map.setZoom(16);


        setCenter({ lat: facility.lat, lng: facility.lng });
        setZoom(18);
        setTimeout(toggleRows, 100);
    };



    useEffect(() => {

        // Run all asynchronous fetches
        fetchWebContent();
        fetchFacilities();
        fetchCategories();

        setTimeout(() => {
            adjustOnResize(); // Apply layout adjustments with a slight delay
        }, 100);

        const handleResize = () => {
            adjustOnResize();
        };

        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, [language, isLoaded, scopeKey, contentKey, vocabularyId, googleMapAPIKey]);


    // **New useEffect to fit bounds only on first load**
    useEffect(() => {
        if (isLoaded && facilities.length > 0 && map) {
            const bounds = new window.google.maps.LatLngBounds();
            facilities.forEach((facility) => bounds.extend({ lat: facility.lat, lng: facility.lng }));

            map.fitBounds(bounds); // Fit all markers initially

            // Force zoom level after fitBounds() is applied
            setTimeout(() => {
                const desiredZoom = window.innerWidth <= 767 ? 9 : 8;
                map.setZoom(desiredZoom);  // Set zoom manually
                setZoom(desiredZoom);      // Update React state
            }, 500); // Short delay ensures it happens after fitBounds()
        }
    }, [isLoaded, facilities, map]); // Runs only when facilities load


    const handleSearchQueryChange = (query) => {
        setSearchQuery(query);

        if (query) {
            const results = facilities.filter((facility) =>
                facility.name &&
                typeof facility.name === "string" &&
                facility.name.toLowerCase().includes(query.toLowerCase())
            );

            // Update autocomplete suggestions
            setAutocompleteResults(results);
            setShowResults(true); // Show autocomplete results
        } else {
            // Clear autocomplete suggestions when search is cleared
            setAutocompleteResults([]);
            setShowResults(false);
        }
    };


    const filteredFacilities = facilities.filter(
        (facility) =>
            facility.name.toLowerCase().includes(searchQuery.toLowerCase()) &&
            (!categoryFilter || facility.category.includes(categoryFilter))
    );

    const animatePanBy = (mapInstance, dx, dy = 0, duration = 600) => {
        if (!mapInstance || (dx === 0 && dy === 0)) return;

        const start = performance.now();
        let lastProgress = 0;
        const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);

        const step = (now) => {
            const elapsed = now - start;
            const rawProgress = Math.min(elapsed / duration, 1);
            const progress = easeOutCubic(rawProgress);
            const deltaProgress = progress - lastProgress;
            const deltaX = dx * deltaProgress;
            const deltaY = dy * deltaProgress;

            try {
                mapInstance.panBy(deltaX, deltaY);
            } catch (e) {
                // ignore when not ready
            }

            lastProgress = progress;
            if (rawProgress < 1) {
                requestAnimationFrame(step);
            }
        };

        requestAnimationFrame(step);
    };


    const handleAutocompleteSelect = (facility) => {
        // Update search query and clear autocomplete results
        setSearchQuery(facility.name);
        setAutocompleteResults([]);
        setShowResults(false);
        setSelectedFacility(facility);
        setTimeout(() => {
            toggleRows();
        }, 100);
        // Dynamically update the filter to match the selected facility's category
        const selectedCategory = facility.category.split(", ")[0]; // Assuming categories are comma-separated
        setCategoryFilter(selectedCategory);

        // Update map center and zoom to focus on the selected facility
        setCenter({
            lat: facility.lat,
            lng: facility.lng,
        });
        setZoom(14); // Adjust zoom level for better visibility
    };


    // place this at top-level of component (so it persists across renders)
    let fitBoundsTimer = null;

    const isHospitalCategory = (cat) => {
        if (!cat) return false;
        const hospitalKeywords = ["Hospitals", "المستشفيات", "Hospital", "المستشفيا"];
        return hospitalKeywords.some(k => cat.includes(k));
    };

    const handleCategoryFilter = (category) => {
        const newFilter = categoryFilter === category ? '' : category;
        setCategoryFilter(newFilter);

        const filtered = facilities.filter(f => !newFilter ? true : f.category.includes(newFilter));

        if (!map || facilities.length === 0) return;

        const isMobile = window.innerWidth <= 767;

        // Mobile behavior — center on first filtered item and apply mobile offset
        if (isMobile) {
            const focus = filtered.length > 0 ? filtered[0] : facilities[0];
            if (focus) {
                const mobileZoom = isHospitalCategory(category) ? 8 : 9;

                const shifted = applyMobileTopOffset(
                    map,
                    { lat: focus.lat, lng: focus.lng },
                    0.4
                );

                map.setZoom(mobileZoom);
                setCenter(shifted);
                setZoom(mobileZoom);
            }
            return;
        }


        if (fitBoundsTimer) {
            clearTimeout(fitBoundsTimer);
            fitBoundsTimer = null;
        }

        fitBoundsTimer = setTimeout(() => {
            const bounds = new window.google.maps.LatLngBounds();
            (filtered.length > 0 ? filtered : facilities).forEach(f => bounds.extend({ lat: f.lat, lng: f.lng }));


            map.fitBounds(bounds);

            // After fitBounds finished (idle), apply offset but DON'T force a conflicting zoom immediately.
            window.google.maps.event.addListenerOnce(map, 'idle', () => {
                // optional: gently clamp zoom (only reduce zoom, don't jump up)
                const currentZoom = map.getZoom();
                const desiredZoom = isHospitalCategory(category) ? 8 : 10;
                if (typeof currentZoom === 'number' && currentZoom > desiredZoom) {
                    map.setZoom(desiredZoom);
                }
            });
        }, 120); // small debounce window
    };



    return (isLoaded && center) ? (
        <section className="w-100 clearfix dhMap" id="dhMap">
            <div className="dhMapWraper">
                <div className="location_wrapper">
                    <div className="map_wrapper">
                        <GoogleMap
                            id="dvMap"
                            center={center}
                            zoom={zoom}
                            options={{
                                panControl: false,
                                zoomControl: true,
                                mapTypeControl: false,
                                scaleControl: false,
                                streetViewControl: false,
                                overviewMapControl: false,
                                rotateControl: false,
                                fullscreenControl: true,
                                mapTypeId: google.maps.MapTypeId.ROADMAP,
                            }}
                            onLoad={(mapInstance) => setMap(mapInstance)}
                            onUnmount={() => setMap(null)}
                        >
                            {filteredFacilities && filteredFacilities.length>0 && filteredFacilities.map((facility) => (

                                <Marker
                                    key={facility.id}
                                    position={{ lat: facility.lat, lng: facility.lng }}
                                    onClick={() => handleMarkerClick(facility)}
                                />
                            ))}
                            {selectedFacility && (
                                <InfoWindow
                                    position={{ lat: selectedFacility.lat, lng: selectedFacility.lng }}
                                    onCloseClick={() => setSelectedFacility(null)}
                                    options={{
                                        pixelOffset: new window.google.maps.Size(0, -30), // Moves it closer to the marker
                                    }}
                                >
                                    <div className="infoWindow">
                                        <h5 className="infoWindowTitle">{selectedFacility.name}</h5>
                                        <a
                                            href={`https://www.google.com/maps?q=${selectedFacility.lat},${selectedFacility.lng}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="infoWindowLink"
                                        >
                                            <svg className="mapIcon" width="11" height="16" viewBox="0 0 11 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path fill-rule="evenodd" clip-rule="evenodd" d="M1.48329 4.93183C2.00811 2.59215 3.8032 1.56784 5.59627 1.65089C7.40841 1.73481 9.20539 2.95055 9.68619 5.17872C9.84399 5.90999 9.71182 6.70318 9.40037 7.46609C8.48167 9.71646 6.98231 11.8528 5.47034 13.6894C4.1036 11.9173 2.69047 9.77217 1.82262 7.6985C1.44197 6.78897 1.28327 5.82355 1.48329 4.93183ZM10.8067 4.93693C10.2063 2.15468 7.93276 0.611582 5.6493 0.505829C3.34679 0.399193 1.02126 1.75437 0.364803 4.68094C0.0981004 5.86993 0.322602 7.08348 0.765201 8.14104C1.78399 10.5754 3.48466 13.0522 5.00874 14.9573L5.44289 15.5L5.89299 14.9705C7.58028 12.9854 9.38354 10.5401 10.4616 7.89935C10.8295 6.99823 11.028 5.96257 10.8067 4.93693ZM4.31002 5.65824C4.31002 4.96185 4.87455 4.39732 5.57093 4.39732C6.26732 4.39732 6.83185 4.96185 6.83185 5.65824C6.83185 6.35462 6.26732 6.91915 5.57093 6.91915C4.87455 6.91915 4.31002 6.35462 4.31002 5.65824ZM5.57093 3.25104C4.24147 3.25104 3.16374 4.32878 3.16374 5.65824C3.16374 6.9877 4.24147 8.06544 5.57093 8.06544C6.90039 8.06544 7.97813 6.9877 7.97813 5.65824C7.97813 4.32878 6.90039 3.25104 5.57093 3.25104Z" fill="#002F70"/>
                                            </svg>
                                            View on Google Maps
                                        </a>
                                    </div>
                                </InfoWindow>
                            )}

                        </GoogleMap>

                        {/* Modal Box Wrapper */}
                        <div className="serch_box_wrapper">
                            <div className="map_box_wrapper">
                                {/* First Modal */}
                                {!selectedFacility && (
                                    <div className="dhLocationBox1 dhLocationScroll">
                                        <div className="dhFacilitiesTme">
                                            <div className="dhLocationBoxInner">
                                                <div className="dhLocationBoxhead">
                                                    <h1>{Liferay.Language.get('where-you-can-find-us')}</h1>
                                                    <button data-bs-toggle="collapse" data-bs-target=".mapCollapse" className="d-block d-md-none">
                                                        <img src="/o/dubai-health-interactive-map/images/down-up.png" alt="down-up" className="img-fluid"></img>
                                                    </button>
                                                </div>
                                                <div className="search_box">
                                                    <div className="topSliderSearch">
                                                        <form className="searchForm">
                                                            <div className={`input-group ${searchQuery ? 'inputActive' : ''}`}>
                                                                <div className="input-group-prepend sliderSearchIcon">
                                   <span className="input-group-text">
                                     <i className="fa fa-search" aria-hidden="true"></i>
                                   </span>
                                                                </div>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    placeholder={Liferay.Language.get('search-by-facility')}
                                                                    value={searchQuery}
                                                                    onChange={(e) => handleSearchQueryChange(e.target.value)}
                                                                />
                                                                <div className="input-group-prepend sliderSearchClose">
                                   <span
                                       className="input-group-text"
                                       onClick={() => {
                                           setSearchQuery(''); // Clear search box
                                           setAutocompleteResults([]); // Clear autocomplete suggestions
                                           setShowResults(false); // Hide the search results
                                       }}
                                   >
                                     <span className="material-symbols-outlined">close</span>
                                   </span>
                                                                </div>
                                                            </div>
                                                        </form>
                                                        {autocompleteResults.length > 0 && (
                                                            <div
                                                                className="searchResult"
                                                                style={{ display: showResults ? 'block' : 'none' }}
                                                            >
                                                                <div className="searchResultInner">
                                                                    <div className="searchResultContent">
                                                                        <h5>{Liferay.Language.get('facilities')}</h5>
                                                                        <ul className="nav flex-column">
                                                                            {autocompleteResults.map((facility) => (
                                                                                <li
                                                                                    key={facility.id}
                                                                                    className="nav-item"
                                                                                    onClick={() => handleAutocompleteSelect(facility)}
                                                                                >
                                                                                    {facility.name}
                                                                                </li>
                                                                            ))}
                                                                        </ul>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                <div className="btn_inner_wrapper">
                                                    <div className="btn_inner_box">
                                                        {categories.map((category) => (
                                                            <button
                                                                key={`${category.id}-${categoryFilter}`} // Add a unique key based on category and filter state
                                                                className={`map_tags_btn ${categoryFilter === category.name ? 'active' : ''}`}
                                                                onClick={() => handleCategoryFilter(category.name)}
                                                                disabled={categoryFilter === category.name}
                                                            >
                                                                {category.name}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="hrHroziLineOuterInner">
                                                <hr className="hrHroziLine" />
                                            </div>
                                            <div dangerouslySetInnerHTML={{ __html: mapCollapseContent }} className="mapCollapse collapse" />
                                        </div>
                                    </div>
                                )}

                                {/* Second Modal */}
                                {selectedFacility && (
                                    <div className="dhLocationBox2 dhLocationScroll">
                                        <div className="dhFacilitiesTme">
                                            <div className="dhLocationBoxInner">
                                                <div className="dhLocationBoxhead">


                                                    <a
                                                        href="#"
                                                        className="readMoreLink maplinkClrBlue"
                                                        onClick={(e) => handleBack(e)}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') handleBack(e);
                                                        }}
                                                    >
                                                        <span
                                                            className="material-symbols-outlined icon">arrow_back_ios</span> {Liferay.Language.get('back')}
                                                    </a>


                                                    <button
                                                        data-bs-toggle="collapse"
                                                        data-bs-target=".outerCollapse"
                                                        className="d-block d-md-none"
                                                        aria-expanded="false"
                                                        aria-controls="outerCollapse"
                                                    >
                                                        <img
                                                            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAICAYAAADJEc7MAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABgSURBVHgBhdDREYAgDAPQhEkYwRXcyFHcyBUcoZuoRfjh2tAvLuRxPYhotqPiwdXOxI77tLlCgWpPLMIUaBRrhJkiL/rMWcdM0Xg9uaNEYpuyRD6e/atb636mQPycwHgBICJCDyq1qt4AAAAASUVORK5CYII="
                                                            alt="down-up"
                                                            className="img-fluid"
                                                        />
                                                    </button>
                                                </div>
                                                <div className="dhLocationDetail">
                                                    <p className="mb-0">{selectedFacility.name}</p>
                                                    <p className="mb-0 hosp_title">
                                                        {selectedFacility.address}
                                                    </p>
                                                    <div className="VisitBtnLink">
                                                        <a href={`https://www.google.com/maps?q=${selectedFacility.lat},${selectedFacility.lng}`}
                                                           className="readMoreLink maplinkClrBlue d-flex d-md-none"
                                                           target="_blank"
                                                           rel="noopener noreferrer"
                                                        >
                                                            {Liferay.Language.get('get-direction')}
                                                            <span className="material-symbols-outlined icon">
                                    arrow_forward_ios
                                  </span>
                                                        </a>
                                                    </div>
                                                    <div className="VisitBtnLink">
                                                        <a href={`/facility/${selectedFacility.friendlyUrlPath}`}
                                                           className="readMoreLink maplinkClrBlue"
                                                           onClick={(e) => {
                                                               e.preventDefault();
                                                               window.location.href = `/facility/${selectedFacility.friendlyUrlPath}`; // Redirect to the facility page
                                                           }
                                                           }
                                                        >
                                                            {Liferay.Language.get('visit-facility-page')}
                                                            <span className="material-symbols-outlined icon">
                                   arrow_forward_ios
                                 </span>
                                                        </a>
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="outerCollapse collapse">
                                                <div className="dhLocationBoxInner">
                                                    {selectedFacility.phone && (
                                                        <div className="dhLocationDetail dhLocationDetail2">
                                                            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABUAAAAVCAYAAACpF6WWAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAHsSURBVHgBpVS7UgJBEOxbyvyMLDIMtXxghhlmZmpkiGaGS6YR5xd4hkbiF4hfoGRkgj/ghZoRmujaMwvnISjeOdQWu83Q0zuPBbJWtSFWbRX/tCDdbTQbCFzMXciV8Jcm+nEHBcx4QtsiYRsOA5IdEBlyf4sqAxVSKtddwCP3HQziA0UlDQ733FXosUzFwzykhoQt+Osep6iQ+HOId+wjpxlVI9f+ruYDe/pdYsACpKJycwJdt0fEIq5LBntATjNU2VW1VVtP0YBnbxEKmOGnDan2RyZ3HpMUtFDASnjpvWGptkJ1hyjXrvT80hsSWyRmiXV5TvKQmoyykMps5pcIkm+Ha6amghyWmSjb5mmPa4vFSRQTMqc9LC22k+J/Uup3XqVOkg11LyRCJreQYfiuWPzWWOCx/5RS71TXPzvEeIqbGbySTphjWgxumKqGtp03Bg8i9C9u5FCaIJWClGsS6JSFAl573RE+JH5HQl88QPp4V/s4wBnPda4TlLcT+g4mlX4pi0jQmlI8Vi0FNegzPe00DZm3YjZplthP3Pwirdt9BroVX/OjUz+O0kfG4Vmfx9/sa9STAPPMX1cCNOD7tkNFlxPK9T1Wn3MRM590NrneZbQqkEKNCL3ovOZ7tc4AR/rwOKqXiRwXjfYJQ22yQqePXNQAAAAASUVORK5CYII=" alt="phone" className="img-fluid" />
                                                            <p className="mb-0">{selectedFacility.phone}</p>
                                                        </div>
                                                    )}
                                                    {selectedFacility.emergencyTimings && (
                                                        <div className="EmergencyBoxhead">
                                                            <h5 className="sub_title">{Liferay.Language.get('emergency-timings')}</h5>
                                                            <div className="cardBoxBadge">
                                                                <a
                                                                    href="#"
                                                                    className="cardBoxBadgeItem badge badge-yellow"
                                                                >
                                                                    <svg
                                                                        width="20"
                                                                        height="20"
                                                                        viewBox="0 0 20 20"
                                                                        fill="none"
                                                                        xmlns="http://www.w3.org/2000/svg"
                                                                    >
                                                                        <path
                                                                            d="M18.9166 9.58333L17.2505 11.25L15.5833 9.58333M17.4542 10.8333C17.4845 10.5597 17.5 10.2817 17.5 10C17.5 5.85786 14.1421 2.5 10 2.5C5.85786 2.5 2.5 5.85786 2.5 10C2.5 14.1421 5.85786 17.5 10 17.5C12.3561 17.5 14.4584 16.4136 15.8333 14.7144M10 5.83333V10L12.5 11.6667"
                                                                            stroke="#0A233C"
                                                                            strokeWidth="1.5"
                                                                            strokeLinecap="round"
                                                                            strokeLinejoin="round"
                                                                        />
                                                                    </svg>
                                                                    {selectedFacility.emergencyTimings}
                                                                </a>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="dhLocationBoxInner">
                                                    <div className="hours_inner_wrap">
                                                        {selectedFacility.visitingHours.length > 0 && (
                                                            <div className="hours_wrap">
                                                                <div className="subtitle_box">
                                                                    <h5>{Liferay.Language.get('visiting-patient-hours')}</h5>
                                                                    <button
                                                                        data-bs-toggle="collapse"
                                                                        data-bs-target=".innerCollapse"
                                                                        className="d-block d-md-none innerCollapseBtn"
                                                                        aria-expanded="false"
                                                                        aria-controls="innerCollapse"
                                                                        onClick={toggleRows}
                                                                    >
                                                                        <img
                                                                            src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA4AAAAICAYAAADJEc7MAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAABgSURBVHgBhdDREYAgDAPQhEkYwRXcyFHcyBUcoZuoRfjh2tAvLuRxPYhotqPiwdXOxI77tLlCgWpPLMIUaBRrhJkiL/rMWcdM0Xg9uaNEYpuyRD6e/atb636mQPycwHgBICJCDyq1qt4AAAAASUVORK5CYII="
                                                                            alt="down-up"
                                                                            className="img-fluid"
                                                                        />
                                                                    </button>
                                                                </div>
                                                                <div className="visitingPatientHours visitingPatientLocationHours">
                                                                    <div className="innerCollapse collapse">
                                                                        {selectedFacility.visitingHours.map((hour, index) => (
                                                                            <div key={index} className="visitingRow">
                                                                                {hour.open24hours ? (
                                                                                    <div className="visitingPatientHoursItemGroup">
                                                                                        <div className="visitingPatientHoursItem">
                                                                                            <div className="visitingPatientHoursIcon">
                                                                                                <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIdSURBVHgBvVU7dtQwFL1yQj80pMTTcjix6abDS5glmBVE6ejGU1GirADoKENHh9NNOWQDER3phgUQ5z5J9kxiO3aSk9zCsn73Pul9BDwx1OCKNzrFPjKuTNiLw+gGFc4Q4RRrYx8m8FZn2MOCf1kY+UNSG3ZN+E3CeMn+MYXW4wUO9WfOaP5Ztt/YGhJsbqxJdezEK2dEzLbAuVkOCyT6C785Z07YFi3iLqS6CEJf8dt86F94qBcUqNyGzvnjI67RvSKyV06/g6j5E2cqWqywpNVFJ4mq5vzOO+dkT8VTy9Wm9F9L4AVE2faSj0FEA4XDX1cYEtQOE+sfA/FX5YIiI2e6Fbhqjl3isYgYcZ4z812BwnvRH0qaUfBRZ0NiNj6Y8Gj/RhEovA5XehfED9NdgXFQ+AGfVBcuX4aFgkDFMiCWDWFtDNdNgyNzJ9SdF1JKNlsBuX+xLNWTESKWJSFvhG7fgT+VRFAp3f1wjpKLxfNiTYEx8AGRt8b/M3oix3kq3W0tSvQvdwqFd6PqTx8SfeGu/NxkwK6TfZLFPMUCD4XUMuGI0Phlr5n8u7I4mL10teRgBlyuznAfCPm2ln1vCwguVz9JPr2XiATGq9kn7vno3o61uRFVqmdTXd8tr2xJM8pWlgvxFY7IkMP77uQ2eb+AJ0hJYEIZEUgo186PUb/P/m2Wh6nsohl+9H1cz0PxqvPEklQEy763+NlwDZQos9DwW9AKAAAAAElFTkSuQmCC" alt="clock" class="img-fluid" />
                                                                                            </div>
                                                                                            <div className="visitingPatientHoursIcon">
                                                                                                <p className="mb-0">{hour.day}: {Liferay.Language.get('open-24-hours')}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                ) : hour.hasSecondSlot ? (
                                                                                    <>
                                                                                        {/* Row 1: Day Name */}
                                                                                        <div className="visitingPatientHoursGroup">
                                                                                            <div className="visitingPatientHours">
                                                                                                <p>{hour.day}</p>

                                                                                                {/* Row 2: Two Time Slots */}
                                                                                                <div className="visitingPatientHoursTwoItemGroup">
                                                                                                    <div className="visitingPatientHoursItem">
                                                                                                        <div className="visitingPatientHoursIcon">
                                                                                                            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIdSURBVHgBvVU7dtQwFL1yQj80pMTTcjix6abDS5glmBVE6ejGU1GirADoKENHh9NNOWQDER3phgUQ5z5J9kxiO3aSk9zCsn73Pul9BDwx1OCKNzrFPjKuTNiLw+gGFc4Q4RRrYx8m8FZn2MOCf1kY+UNSG3ZN+E3CeMn+MYXW4wUO9WfOaP5Ztt/YGhJsbqxJdezEK2dEzLbAuVkOCyT6C785Z07YFi3iLqS6CEJf8dt86F94qBcUqNyGzvnjI67RvSKyV06/g6j5E2cqWqywpNVFJ4mq5vzOO+dkT8VTy9Wm9F9L4AVE2faSj0FEA4XDX1cYEtQOE+sfA/FX5YIiI2e6Fbhqjl3isYgYcZ4z812BwnvRH0qaUfBRZ0NiNj6Y8Gj/RhEovA5XehfED9NdgXFQ+AGfVBcuX4aFgkDFMiCWDWFtDNdNgyNzJ9SdF1JKNlsBuX+xLNWTESKWJSFvhG7fgT+VRFAp3f1wjpKLxfNiTYEx8AGRt8b/M3oix3kq3W0tSvQvdwqFd6PqTx8SfeGu/NxkwK6TfZLFPMUCD4XUMuGI0Phlr5n8u7I4mL10teRgBlyuznAfCPm2ln1vCwguVz9JPr2XiATGq9kn7vno3o61uRFVqmdTXd8tr2xJM8pWlgvxFY7IkMP77uQ2eb+AJ0hJYEIZEUgo186PUb/P/m2Wh6nsohl+9H1cz0PxqvPEklQEy763+NlwDZQos9DwW9AKAAAAAElFTkSuQmCC" alt="clock" class="img-fluid" />
                                                                                                        </div>
                                                                                                        <div className="visitingPatientHoursIcon">
                                                                                                            <p className="mb-0">{hour.firstSlot}</p>
                                                                                                        </div>
                                                                                                    </div>

                                                                                                    <div className="visitingPatientHoursItem">
                                                                                                        <div className="visitingPatientHoursIcon">
                                                                                                            <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIdSURBVHgBvVU7dtQwFL1yQj80pMTTcjix6abDS5glmBVE6ejGU1GirADoKENHh9NNOWQDER3phgUQ5z5J9kxiO3aSk9zCsn73Pul9BDwx1OCKNzrFPjKuTNiLw+gGFc4Q4RRrYx8m8FZn2MOCf1kY+UNSG3ZN+E3CeMn+MYXW4wUO9WfOaP5Ztt/YGhJsbqxJdezEK2dEzLbAuVkOCyT6C785Z07YFi3iLqS6CEJf8dt86F94qBcUqNyGzvnjI67RvSKyV06/g6j5E2cqWqywpNVFJ4mq5vzOO+dkT8VTy9Wm9F9L4AVE2faSj0FEA4XDX1cYEtQOE+sfA/FX5YIiI2e6Fbhqjl3isYgYcZ4z812BwnvRH0qaUfBRZ0NiNj6Y8Gj/RhEovA5XehfED9NdgXFQ+AGfVBcuX4aFgkDFMiCWDWFtDNdNgyNzJ9SdF1JKNlsBuX+xLNWTESKWJSFvhG7fgT+VRFAp3f1wjpKLxfNiTYEx8AGRt8b/M3oix3kq3W0tSvQvdwqFd6PqTx8SfeGu/NxkwK6TfZLFPMUCD4XUMuGI0Phlr5n8u7I4mL10teRgBlyuznAfCPm2ln1vCwguVz9JPr2XiATGq9kn7vno3o61uRFVqmdTXd8tr2xJM8pWlgvxFY7IkMP77uQ2eb+AJ0hJYEIZEUgo186PUb/P/m2Wh6nsohl+9H1cz0PxqvPEklQEy763+NlwDZQos9DwW9AKAAAAAElFTkSuQmCC" alt="clock" class="img-fluid" />
                                                                                                        </div>
                                                                                                        <div className="visitingPatientHoursIcon">
                                                                                                            <p className="mb-0">{hour.secondSlot}</p>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </div>
                                                                                            </div>
                                                                                        </div>
                                                                                    </>
                                                                                ) : (
                                                                                    <div className="visitingPatientHoursItemGroup">
                                                                                        <div className="visitingPatientHoursItem">
                                                                                            <div className="visitingPatientHoursIcon">
                                                                                                <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABgAAAAYCAYAAADgdz34AAAACXBIWXMAAAsTAAALEwEAmpwYAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAIdSURBVHgBvVU7dtQwFL1yQj80pMTTcjix6abDS5glmBVE6ejGU1GirADoKENHh9NNOWQDER3phgUQ5z5J9kxiO3aSk9zCsn73Pul9BDwx1OCKNzrFPjKuTNiLw+gGFc4Q4RRrYx8m8FZn2MOCf1kY+UNSG3ZN+E3CeMn+MYXW4wUO9WfOaP5Ztt/YGhJsbqxJdezEK2dEzLbAuVkOCyT6C785Z07YFi3iLqS6CEJf8dt86F94qBcUqNyGzvnjI67RvSKyV06/g6j5E2cqWqywpNVFJ4mq5vzOO+dkT8VTy9Wm9F9L4AVE2faSj0FEA4XDX1cYEtQOE+sfA/FX5YIiI2e6Fbhqjl3isYgYcZ4z812BwnvRH0qaUfBRZ0NiNj6Y8Gj/RhEovA5XehfED9NdgXFQ+AGfVBcuX4aFgkDFMiCWDWFtDNdNgyNzJ9SdF1JKNlsBuX+xLNWTESKWJSFvhG7fgT+VRFAp3f1wjpKLxfNiTYEx8AGRt8b/M3oix3kq3W0tSvQvdwqFd6PqTx8SfeGu/NxkwK6TfZLFPMUCD4XUMuGI0Phlr5n8u7I4mL10teRgBlyuznAfCPm2ln1vCwguVz9JPr2XiATGq9kn7vno3o61uRFVqmdTXd8tr2xJM8pWlgvxFY7IkMP77uQ2eb+AJ0hJYEIZEUgo186PUb/P/m2Wh6nsohl+9H1cz0PxqvPEklQEy763+NlwDZQos9DwW9AKAAAAAElFTkSuQmCC" alt="clock" class="img-fluid" />
                                                                                            </div>
                                                                                            <div className="visitingPatientHoursIcon">
                                                                                                <p className="mb-0">{hour.day} {hour.firstSlot}</p>
                                                                                            </div>
                                                                                        </div>
                                                                                    </div>
                                                                                )}
                                                                            </div>
                                                                        ))}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        )}
                                                    </div>
                                                    <div className="dhLocationBoxInner">
                                                        <div className="hours_inner_wrap">
                                                            <div className="emergencyDepartment">
                                                                <div className="emergencyDepartmentIcon">
                                                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none"
                                                                         xmlns="http://www.w3.org/2000/svg">
                                                                        <path
                                                                            d="M8.00004 10.6663V7.99967M8.00004 5.33301H8.00671M14.6667 7.99967C14.6667 11.6816 11.6819 14.6663 8.00004 14.6663C4.31814 14.6663 1.33337 11.6816 1.33337 7.99967C1.33337 4.31778 4.31814 1.33301 8.00004 1.33301C11.6819 1.33301 14.6667 4.31778 14.6667 7.99967Z"
                                                                            stroke="#6685A3" stroke-width="1.5" stroke-linecap="round"
                                                                            stroke-linejoin="round" />
                                                                    </svg>
                                                                </div>
                                                                <div className="emergencyDepartmentPara">
                                                                    <p className="mb-0">
                                                                        {Liferay.Language.get('emergency-department-paragraph')}
                                                                        <a href={`/facility/${selectedFacility.friendlyUrlPath}`}
                                                                           className="readMoreLink"
                                                                           onClick={(e) => {
                                                                               e.preventDefault();
                                                                               window.location.href = `/facility/${selectedFacility.friendlyUrlPath}`; // Redirect to the facility page
                                                                           }
                                                                           }
                                                                        >{Liferay.Language.get('here')}</a>.
                                                                    </p>
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    ) :  (
        <p>Loading Interactive Map</p>
    );
}

export default React.memo(MapComponent);
